import React from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, LinearProgress, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SectionCard from './SectionCard';

interface DatosDeLaVentaProps {
	saleAmount?: number;
	totalCost?: number;
	grossProfitPercentage?: number;
	grossProfit?: number;
	chargedAmount?: number;
	pendingPayAmount?: number;
	travelDepartureDate: string;
	currency: string;
	sx?: SxProps<Theme>;
}

const fmt = (value: number): string =>
	new Intl.NumberFormat('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

const collectionUrgency = (
	amount: number | undefined,
	departureDate: string | undefined,
): { text: React.ReactNode; strong: boolean } | null => {
	if (amount === undefined || departureDate === undefined || amount <= 0) return null;

	const today = new Date();
	const departureDateObj = new Date(departureDate);
	const diffTime = departureDateObj.getTime() - today.getTime();
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

	if (diffDays < 0) {
		return {
			strong: true,
			text: (
				<>
					{`PAX VIAJÓ HACE ${-diffDays} ${-diffDays === 1 ? 'DÍA' : 'DÍAS'}`}
					<br />
					{'🤬 URGENTE COBRAR SALDO'}
				</>
			),
		};
	}
	if (diffDays < 30) {
		return {
			strong: true,
			text: (
				<>
					{`${diffDays} ${diffDays === 1 ? 'día' : 'días'} para la fecha de viaje`}
					<br />
					{'COBRAR SALDO 😠'}
				</>
			),
		};
	}
	if (diffDays <= 45) {
		return {
			strong: false,
			text: (
				<>
					{`${diffDays} ${diffDays === 1 ? 'día' : 'días'} para la fecha de viaje`}
					<br />
					{'COBRAR SALDO 😟'}
				</>
			),
		};
	}
	return null;
};

const MetricRow = ({
	label,
	value,
	color,
	bold,
}: {
	label: string;
	value: string;
	color?: string;
	bold?: boolean;
}): JSX.Element => (
	<Box display="flex" justifyContent="space-between" alignItems="baseline" width="100%" gap={1}>
		<Typography variant="body2" color="text.secondary">
			{label}
		</Typography>
		<Typography variant="body2" sx={{ fontWeight: bold ? 700 : 500, color: color ?? 'text.primary' }}>
			{value}
		</Typography>
	</Box>
);

const DatosDeLaVenta: React.FC<DatosDeLaVentaProps> = ({
	saleAmount: saleAmountProp,
	totalCost: totalCostProp,
	grossProfitPercentage: grossProfitPercentageProp,
	grossProfit: grossProfitProp,
	chargedAmount: chargedAmountProp,
	pendingPayAmount: pendingPayAmountProp,
	travelDepartureDate,
	currency,
	sx,
}) => {
	// Órdenes sin cobros (u órdenes viejas) pueden traer estos montos en
	// undefined aunque el tipo diga number — sin esto, fmt() imprime "NaN".
	const saleAmount = Number(saleAmountProp) || 0;
	const totalCost = Number(totalCostProp) || 0;
	const grossProfitPercentage = Number(grossProfitPercentageProp) || 0;
	const grossProfit = Number(grossProfitProp) || 0;
	const chargedAmount = Number(chargedAmountProp) || 0;
	const pendingPayAmount = Number(pendingPayAmountProp) || 0;

	const total = chargedAmount + pendingPayAmount;
	const chargedPercent = total > 0 ? (chargedAmount / total) * 100 : 0;
	const urgency = collectionUrgency(pendingPayAmount, travelDepartureDate);
	const marginColor = grossProfit > 0 ? 'success.main' : grossProfit < 0 ? 'error.main' : 'text.primary';
	const overcharged = pendingPayAmount < 0;

	return (
		<SectionCard
			title="Datos de la venta"
			icon={<QueryStatsIcon fontSize="small" />}
			action={
				grossProfitPercentage > 16 && pendingPayAmount === 0 ? (
					<Typography variant="body2">¡Crack! 🤩</Typography>
				) : undefined
			}
			sx={[{ display: 'flex', flexDirection: 'column', height: '100%' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
		>
			<MetricRow label="Venta" value={`${currency} ${fmt(saleAmount)}`} />
			<MetricRow label="Costo" value={`${currency} ${fmt(totalCost)}`} />
			<MetricRow label="MB %" value={`${grossProfitPercentage}%`} color={marginColor} />
			<MetricRow label="MB" value={`${currency} ${fmt(grossProfit)}`} color={marginColor} bold />

			<Divider sx={{ my: 1 }} />

			<MetricRow label="Monto cobrado" value={`${currency} ${fmt(chargedAmount)}`} />
			<MetricRow
				label="Saldo pendiente"
				value={`${currency} ${fmt(pendingPayAmount)}`}
				color={pendingPayAmount > 0 ? 'warning.main' : overcharged ? 'warning.main' : 'success.main'}
				bold={pendingPayAmount !== 0}
			/>

			<Box sx={{ mt: 'auto', pt: 1.5 }}>
				<Tooltip title={`Cobrado ${currency} ${fmt(chargedAmount)} de ${currency} ${fmt(total)}`}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<LinearProgress
							variant="determinate"
							value={Math.min(100, chargedPercent)}
							color={chargedPercent >= 100 ? 'success' : 'primary'}
							sx={{ height: 8, borderRadius: 4, flex: 1 }}
						/>
						<Typography variant="caption" color="text.secondary" sx={{ minWidth: 44, textAlign: 'right' }}>
							{chargedPercent.toFixed(1)}%
						</Typography>
					</Box>
				</Tooltip>

				{overcharged && (
					<Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.5 }}>
						Cobrado de más: {currency} {fmt(Math.abs(pendingPayAmount))} a favor del cliente
					</Typography>
				)}

				{urgency && (
					<Typography
						variant="body2"
						align="center"
						sx={{ color: 'error.main', fontWeight: urgency.strong ? 700 : 400, mt: 1 }}
					>
						{urgency.text}
					</Typography>
				)}
			</Box>
		</SectionCard>
	);
};

DatosDeLaVenta.propTypes = {
	saleAmount: PropTypes.number,
	totalCost: PropTypes.number,
	grossProfitPercentage: PropTypes.number,
	grossProfit: PropTypes.number,
	chargedAmount: PropTypes.number,
	pendingPayAmount: PropTypes.number,
	travelDepartureDate: PropTypes.string.isRequired,
	currency: PropTypes.string.isRequired,
	sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default DatosDeLaVenta;
